/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import { breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { CoachStateProvider, useCoachState } from 'bundles/unified-common/providers/CoachStateProvider';
import type { QuickPrompt } from 'bundles/wes-common/components/WESAskCoursera/WESAskCourseraEmptyState';
import WESAskCourseraPopup from 'bundles/wes-common/components/WESAskCoursera/WESAskCourseraPopup';
import type { AdditionalContextItem } from 'bundles/wes-common/components/WESAskCoursera/constants';
import { CONTEXT_CONFIG, getActiveCoachModeId } from 'bundles/wes-common/components/WESAskCoursera/constants';

import WESAskCourseraLauncher from './WESAskCourseraLauncher';
import type { LauncherPlacement, Position } from './WESAskCourseraLauncherTypes';

export type { LauncherPlacement } from './WESAskCourseraLauncherTypes';

const styles = {
  // Hide on mobile screens - only show on tablet and desktop
  container: css`
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

export type WESAskCourseraProps = {
  position?: Position;
  launcherPlacement?: LauncherPlacement;
  disabled?: boolean;
  autoOpenDelay?: number; // Delay in seconds before auto-opening (e.g., 3 for 3 seconds). Only opens once per session.
  quickPrompts?: QuickPrompt[];
  rotatingQuestions?: string[];
  onOpen?: () => void;
  onClose?: () => void;
  welcomeMessage?: string;
  launcherVariant?: 'default' | 'inverted';
  pageContext?: AdditionalContextItem[];
};

/**
 * Content component that consumes CoachStateProvider context.
 * Must be rendered inside CoachStateProvider.
 */
const WESAskCourseraContent = ({
  position = 'bottom-right',
  launcherPlacement = 'floating',
  disabled = false,
  autoOpenDelay,
  quickPrompts,
  rotatingQuestions,
  onOpen,
  onClose,
  welcomeMessage,
  launcherVariant = 'default',
  pageContext = [],
}: WESAskCourseraProps) => {
  const { coachOpen, setCoachOpen, setContextConfig } = useCoachState();
  const hasAutoOpened = useRef(false);
  const pendingQuestionRef = useRef<string | null>(null);
  const track = useTracker();

  const activeCoachModeId = getActiveCoachModeId();

  const activeContextConfig = useMemo(
    () => ({
      ...CONTEXT_CONFIG,
      additionalContext: [...CONTEXT_CONFIG.additionalContext, ...pageContext],
    }),
    [pageContext]
  );

  // Set context config on mount so CourseraAiHeader can access it
  useEffect(() => {
    // Prevent infinite loop by not updating if it's deeply equal
    setContextConfig((prevConfig) => {
      // Very basic equality check for our specific object shape
      if (
        prevConfig?.config?.coachModeId === activeContextConfig.config.coachModeId &&
        prevConfig?.additionalContext?.length === activeContextConfig.additionalContext.length
      ) {
        return prevConfig;
      }
      return activeContextConfig;
    });
  }, [activeContextConfig, setContextConfig]);

  const toggleCoach = React.useCallback<() => void>(() => {
    if (disabled) return;

    setCoachOpen((prev) => {
      const nextState = !prev;

      track('interact_coach', {
        coachInteractionType: nextState ? 'launch_coach_tool' : 'close_coach_tool',
        coachAction: 'Ask Coursera Icon',
        coach: {
          mode: activeCoachModeId,
        },
      });

      // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
      track('click_coach_element', {
        clickElementType: nextState ? 'launch_coach_tool' : 'close_coach_tool',
        coachAction: 'Ask Coursera Icon',
        coach: {
          mode: activeCoachModeId,
        },
      });

      if (nextState) {
        onOpen?.();
        hasAutoOpened.current = true;
      } else {
        onClose?.();
      }

      return nextState;
    });
  }, [disabled, onOpen, setCoachOpen, onClose, track, activeCoachModeId]);

  // Auto-open functionality - only opens once per session
  useEffect(() => {
    if (!autoOpenDelay || disabled || hasAutoOpened.current) return undefined;

    const timer = setTimeout(() => {
      setCoachOpen((currentIsOpen) => {
        if (currentIsOpen) return currentIsOpen;

        track('interact_coach', {
          coachInteractionType: 'launch_coach_tool',
          coach: {
            mode: activeCoachModeId,
          },
          coachAction: 'auto_open_coach_tool',
        });

        // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
        track('click_coach_element', {
          clickElementType: 'launch_coach_tool',
          coach: {
            mode: activeCoachModeId,
          },
          coachAction: 'auto_open_coach_tool',
        });

        onOpen?.();
        hasAutoOpened.current = true;

        return true;
      });
    }, autoOpenDelay * 1000);

    return () => clearTimeout(timer);
  }, [autoOpenDelay, disabled, onOpen, setCoachOpen, track, activeCoachModeId]);

  const handleQuestionClick = useCallback<(question: string) => void>(
    (question: string) => {
      pendingQuestionRef.current = question;
      setCoachOpen(true);

      track('interact_coach', {
        coachInteractionType: 'click_action',
        coachAction: 'click_rotating_question',
        coach: {
          mode: activeCoachModeId,
        },
      });

      // Coach eventing consolidation [COACH-2404]: dual-emit onto the new ClickCoachElement schema
      track('click_coach_element', {
        clickElementType: 'action',
        coachAction: 'click_rotating_question',
        coach: {
          mode: activeCoachModeId,
        },
      });

      track('send_coach_message', {
        coachMessageText: question,
        coachAction: 'send_rotating_question',
        coachMessageLength: question.length,
        coach: {
          mode: activeCoachModeId,
        },
      });

      onOpen?.();
      hasAutoOpened.current = true;
    },
    [setCoachOpen, track, activeCoachModeId, onOpen]
  );

  const handleInitialMessageSent = useCallback<() => void>(() => {
    pendingQuestionRef.current = null;
  }, []);

  if (disabled) {
    return null;
  }

  return (
    <>
      <WESAskCourseraLauncher
        colorVariant={launcherVariant}
        isOpen={coachOpen}
        onClick={toggleCoach}
        position={position}
        launcherPlacement={launcherPlacement}
        rotatingQuestions={rotatingQuestions}
        onQuestionClick={handleQuestionClick}
      />
      {coachOpen && (
        <WESAskCourseraPopup
          onClose={toggleCoach}
          contextConfig={activeContextConfig}
          position={position}
          quickPrompts={quickPrompts}
          welcomeMessage={welcomeMessage}
          initialMessage={pendingQuestionRef.current}
          onInitialMessageSent={handleInitialMessageSent}
        />
      )}
    </>
  );
};

/**
 * WESAskCoursera - AI Coach component for WES applications (teams-v2, ent-website).
 * Wraps the inner component with CoachStateProvider to manage state.
 * Note: Hidden on mobile screens - only visible on tablet and desktop.
 */
const WESAskCoursera = (props: WESAskCourseraProps) => {
  return (
    <div css={styles.container}>
      <CoachStateProvider>
        <WESAskCourseraContent {...props} />
      </CoachStateProvider>
    </div>
  );
};

export default WESAskCoursera;
